import microuniversoApi from "app/api/microuniversoApi";
import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container";
import globalMU from "globalMU";
import React, { memo, useEffect, useRef, useState } from "react";
import { IEndereco } from "app/types/IEndereco";
import { tratamentoErroPadrao } from "app/use-cases";
import { NovoEndereco } from "./NovoEnderecoCliente";
import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { ICidade } from "app/types/ICidade";
import { ModalSelecionarCep } from "./modal-selecionar-cep/ModalSelecionarCep";
import { Bairro } from "app/types/NovoEndereco";
import { CepPorEndereco } from "app/types/CepPorEndereco";
import { ComissoesEndereco } from "app/types/ComissoesEndereco";

export type NovoEnderecoClienteContainerProps = {
    endereco: IEndereco;
    clienteNaoCadastrado: boolean
    cpfCnpj: string;
    numeroEBairroObrigatorio: boolean
    utilizarBairrosPreCadastrados: boolean
    ativaEditarEndereco?: (ativa: boolean, endereco: IEndereco) => void;
    adicionarEndereco: (endereco: IEndereco) => void;
    editarEndereco: (endereco: IEndereco) => void;
}


export const NovoEnderecoClienteContainer = memo((props: NovoEnderecoClienteContainerProps) => {
    const isMounted = useRef<boolean>(true)
    const [dadosEditarEndereco, setDadosEditarEndereco] = useState<ComissoesEndereco>({ Classificacoes: null, Estados: null });
    const [cidadesPorEstado, setCidadesPorEstado] = useState<ICidade[]>();
    const [bairros, setBairros] = useState<Bairro[]>([]);
    const [endereco, setEndereco] = useState<IEndereco>(props.endereco);
    const [exibeModal, setExibeModal] = useState(false);
    const [dadosModalEndereco, setDadosModalEndereco] = useState<PagedMessageResponse<CepPorEndereco>>()
    const [adicionandoEndereco, setAdicionandoEndereco] = useState(false);

    useEffect(() => () => { isMounted.current = false }, [])

    const onDadosEdicaoEndereco = async () => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Cliente.dadosEditarEndereco(props.cpfCnpj, props.clienteNaoCadastrado);
            if (isMounted.current && !("HasErrors" in resposta)) {
                setDadosEditarEndereco(resposta)
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }

    const onListarCidadesPorEstado = async (codigoEstado: number) => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Comum.listarCidadesPorEstado(codigoEstado);
            if (isMounted.current && !("HasErrors" in resposta)) {
                setCidadesPorEstado(resposta)
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }

    const onListarBairros = async (bairro: string) => {
        if (!props.utilizarBairrosPreCadastrados) return; // 453510
        try {
            globalMU.blockUI();
            const bairros = await microuniversoApi.Comum.listarBairrosPreCadastrados(bairro);
            if (isMounted.current) {
                !("HasErrors" in bairros) ? setBairros(bairros) : setBairros([])
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    };

    const pesquisaPorCep = async (cep: string) => {
        cep = cep.replace(/\D/g, "") // Remove caracteres não numéricos
        if (cep.length <= 0) {
            return
        }
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Comum.obterEnderecoPorCep(cep?.trim());
            if (isMounted.current && !("HasErrors" in resposta)) {
                setEndereco({
                    ...endereco,
                    Logradouro: resposta.Logradouro,
                    Bairro: resposta.Bairro,
                    Cep: resposta.Cep,
                    Cidade: resposta.Cidade,
                    Estado: resposta.Estado,
                    NomeCidade: resposta.NomeCidade,
                    CodigoCidade: resposta.CodigoCidade,
                    SiglaUf: resposta.SiglaUf,
                    CodigoEstado: resposta.CodigoEstado
                })
                onListarCidadesPorEstado(resposta.Estado.Codigo);
                onListarBairros(resposta.Bairro);
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }

    const pesquisaPorEndereco = async (numeroPagina?: number) => {
        try {
            globalMU.blockUI();
            const resposta = await microuniversoApi.Comum.obterCepPorEndereco({
                Bairro: endereco.Bairro,
                Logradouro: endereco.Logradouro,
                NomeCidade: endereco.Cidade != null ? endereco.Cidade.Nome : "",
                NumeroPagina: numeroPagina ?? 1,
                SiglaUf: endereco.Estado?.Sigla ?? "",
                TamanhoPagina: 20,
            });
            if (isMounted.current && !("HasErrors" in resposta)) {
                setDadosModalEndereco(resposta)
                resposta?.Entities?.length > 1 && setExibeModal(true);
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            globalMU.unblockUI();
        }
    }

    const onAdicionarOuAtualizarEndereco = (endereco: IEndereco) => {
        try {
            if (!isMounted) return;
            setAdicionandoEndereco(true)
            if (props.endereco == null) {
                props.adicionarEndereco(endereco);
                onVoltarNovoEndereco();
            }
            else {
                props.editarEndereco(endereco);
                onVoltarNovoEndereco();
            }
        } catch (e) {
            tratamentoErroPadrao(e);
        } finally {
            if (isMounted.current) {
                setAdicionandoEndereco(false)
            }
        }
    }

    const atualzaDadosASerSalvos = (endereco: IEndereco) => {
        if (isMounted.current) {
            setEndereco(endereco)
        }
    }

    const onVoltarCadastroCliente = () => {
        props.ativaEditarEndereco(false, null);
    }

    const onVoltarNovoEndereco = () => {
        props.ativaEditarEndereco(false, null);
    }

    useEffect(() => {
        onDadosEdicaoEndereco();
        props.endereco && onListarCidadesPorEstado(props.endereco?.CodigoEstado);
        props.endereco?.Bairro && onListarBairros(props.endereco?.Bairro);
    }, [])

    return (
        <>
            <Breadcrumb2Container
                id="NovoEnderecoClienteContainer"
                passos={[props.clienteNaoCadastrado ? "Cliente Não Cadastrado" : "Cadastro Cliente", props.endereco == null ? "Novo Endereço" : "Editar Endereço"]}
                onPassoSelecionado={passo => {
                    if (passo === "Cadastro Cliente" || passo === "Cliente Não Cadastrado") {
                        onVoltarCadastroCliente()
                    }
                    if (passo === "Novo Endereço" || passo === "Editar Endereço") {
                        onVoltarNovoEndereco()
                    }
                }}
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onBotaoVoltarClicado={onVoltarCadastroCliente}
            />

            <NovoEndereco
                numeroEBairroObrigatorio={props.numeroEBairroObrigatorio}
                pesquisaPorEndereco={pesquisaPorEndereco}
                onErrorValidacao={(mensagemErroNovoEndereco) => globalMU.showErrorMessage(mensagemErroNovoEndereco)}
                bairros={bairros}
                estados={dadosEditarEndereco.Estados}
                cidades={cidadesPorEstado}
                opcoesTipo={dadosEditarEndereco.Classificacoes}
                endereco={endereco}
                utilizarBairrosPreCadastrados={props.utilizarBairrosPreCadastrados}
                adicionandoEndereco={adicionandoEndereco}
                onAdicionar={onAdicionarOuAtualizarEndereco}
                onPesquisarCep={pesquisaPorCep}
                onListarCidadesPorEstado={onListarCidadesPorEstado}
                atualzaDadosASerSalvos={atualzaDadosASerSalvos}
                onListarBairros={onListarBairros}
                dadosModalEndereco={dadosModalEndereco}
            />

            {dadosModalEndereco &&
                <ModalSelecionarCep
                    dados={dadosModalEndereco}
                    open={exibeModal}
                    onFecharModal={() => setExibeModal(false)}
                    onEnderecoSelecionado={(endereco) => pesquisaPorCep(endereco.Cep)}
                    onAlterarListaSelecionadaEndereco={pesquisaPorEndereco}
                />
            }
        </>
    )
})